import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Send } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PasskeySettingCard } from "./PasskeySettingCard";
import { OrgTelegramAlerts } from "./OrgTelegramAlerts";
import { OrgWhatsappAlerts } from "./OrgWhatsappAlerts";
import { OutlookSettingsCard } from "./OutlookPanel";
import { EmployeeFieldsSettings } from "./EmployeeFieldsSettings";
import { EmailDigestCard } from "./EmailDigestCard";
import { PortalIdLoginCard } from "./PortalIdLoginCard";

export const SettingsDialog = ({ open, onOpenChange, settings, onSave, onTest, onTestEmail, embedded = false }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if ((open || embedded) && settings) setForm({ ...settings });
  }, [open, settings, embedded]);

  if (!form) return null;

  const dirty = settings && (
    (form.notify_phone || "") !== (settings.notify_phone || "") ||
    form.days_before !== settings.days_before ||
    form.second_reminder_days !== settings.second_reminder_days ||
    form.enabled !== settings.enabled ||
    Boolean(form.email_enabled) !== Boolean(settings.email_enabled) ||
    (form.notify_email || "") !== (settings.notify_email || "") ||
    (form.contract_alert_days ?? 60) !== (settings.contract_alert_days ?? 60) ||
    JSON.stringify(form.employee_custom_fields || []) !== JSON.stringify(settings.employee_custom_fields || [])
  );

  const body = (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 py-2 items-start">
          <div className="space-y-1.5">
            <Label htmlFor="days-before">{t("dash.modals.settings.daysBefore")}</Label>
            <Input id="days-before" type="number" min={1} max={90} data-testid="days-before-input"
              value={form.days_before}
              onChange={(e) => setForm({ ...form, days_before: parseInt(e.target.value || "30", 10) })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="second-reminder">{t("dash.modals.settings.secondReminder")}</Label>
            <Input id="second-reminder" type="number" min={0} max={30} data-testid="second-reminder-input"
              value={form.second_reminder_days}
              onChange={(e) => setForm({ ...form, second_reminder_days: parseInt(e.target.value || "1", 10) })} />
            <p className="text-xs text-slate-400">{t("dash.modals.settings.secondHint")}</p>
          </div>
          <EmployeeFieldsSettings form={form} setForm={setForm} />
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3 sm:col-span-2">
            <div>
              <p className="text-sm font-semibold">{t("dash.modals.settings.autoEnable")}</p>
              <p className="text-xs text-slate-500">{t("dash.modals.settings.autoHint")}</p>
            </div>
            <Switch data-testid="alerts-enabled-switch" checked={form.enabled}
              className="data-[state=checked]:bg-blue-600"
              onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
          </div>

          <div className="pt-2 border-t border-slate-100 space-y-3 sm:col-span-2">
            <p className="text-sm font-semibold flex items-center gap-1.5">🛢️ حدود تغيير الزيت (كم)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="oil-km-car">السيارات</Label>
                <Input id="oil-km-car" type="number" min={100} dir="ltr" data-testid="oil-km-car-input"
                  value={form.oil_change_km_car ?? 4500}
                  onChange={(e) => setForm({ ...form, oil_change_km_car: parseInt(e.target.value || "4500", 10) })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="oil-km-moto">الدراجات النارية</Label>
                <Input id="oil-km-moto" type="number" min={100} dir="ltr" data-testid="oil-km-moto-input"
                  value={form.oil_change_km_moto ?? 1000}
                  onChange={(e) => setForm({ ...form, oil_change_km_moto: parseInt(e.target.value || "1000", 10) })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="oil-alert-km">تنبيه تيليجرام للسيارات قبل الحد بـ (كم)</Label>
                <Input id="oil-alert-km" type="number" min={0} dir="ltr" data-testid="oil-alert-km-input"
                  value={form.oil_alert_km ?? 500}
                  onChange={(e) => setForm({ ...form, oil_alert_km: parseInt(e.target.value || "0", 10) })} />
              </div>
            </div>
            <p className="text-xs text-slate-400">إذا كانت المسافة منذ آخر تغيير أقل من الحد، يرفض النظام التسجيل؛ وإن زادت يُسجّل مع تنبيه بالتجاوز.</p>
          </div>

          <div className="pt-2 border-t border-slate-100 space-y-3 sm:col-span-2">
            <p className="text-sm font-semibold flex items-center gap-1.5">⛽ حد انحراف استهلاك الوقود (%)</p>
            <div className="space-y-1.5">
              <Label htmlFor="fuel-threshold">نسبة الانحراف للتنبيه</Label>
              <Input id="fuel-threshold" type="number" min={1} max={100} dir="ltr" data-testid="fuel-threshold-input"
                value={form.fuel_efficiency_threshold ?? 15}
                onChange={(e) => setForm({ ...form, fuel_efficiency_threshold: parseInt(e.target.value || "15", 10) })} />
            </div>
            <p className="text-xs text-slate-400">يقارن النظام استهلاك المندوب الفعلي بالمعيار حسب نوع المركبة (سيارة 1.07 طلب/لتر · دباب صيني 2 · دباب بوكسر 1.5). أي انحراف يتجاوز هذه النسبة يظهر كتنبيه في مقارنة أداء المندوب.</p>
          </div>

          <div className="pt-2 border-t border-slate-100 space-y-3 sm:col-span-2">
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <div>
                <p className="text-sm font-semibold">{t("dash.modals.settings.emailBackup")}</p>
                <p className="text-xs text-slate-500">{t("dash.modals.settings.emailBackupHint")}</p>
              </div>
              <Switch data-testid="email-enabled-switch" checked={form.email_enabled}
                className="data-[state=checked]:bg-blue-600"
                onCheckedChange={(v) => setForm({ ...form, email_enabled: v })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notify-email">{t("dash.modals.settings.email")}</Label>
              <Input id="notify-email" data-testid="notify-email-input" dir="ltr" type="email"
                value={form.notify_email || ""} placeholder="name@example.com"
                onChange={(e) => setForm({ ...form, notify_email: e.target.value })} />
            </div>
            <Button variant="outline" data-testid="email-test-send-button" disabled={dirty}
              className="w-full border-blue-500 text-blue-700 hover:bg-blue-50" onClick={onTestEmail}>
              <Send className="h-4 w-4 mx-2" /> {t("dash.modals.settings.testEmail")}
            </Button>
            {dirty && <p className="text-xs text-amber-600 text-center">{t("dash.modals.settings.saveFirst")}</p>}
          </div>
          <EmailDigestCard settings={settings} />
          <PortalIdLoginCard settings={settings} />
          <OrgWhatsappAlerts />
          <OrgTelegramAlerts />
          <OutlookSettingsCard />
          <div className="pt-2 border-t border-slate-100 space-y-3 sm:col-span-2">
            <p className="text-sm font-semibold flex items-center gap-1.5">⚠️ نصوص إنذارات التارقت (بوابة المندوب)</p>
            <div className="space-y-1.5">
              <Label htmlFor="tw-title">عنوان الإنذار</Label>
              <Input id="tw-title" data-testid="target-warn-title-input" value={form.target_warn_title ?? ""}
                onChange={(e) => setForm({ ...form, target_warn_title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tw-body">نص الإنذار (استخدم {"{month}"} لإدراج اسم الشهر)</Label>
              <textarea id="tw-body" data-testid="target-warn-body-input" rows={2} value={form.target_warn_body ?? ""}
                onChange={(e) => setForm({ ...form, target_warn_body: e.target.value })}
                className="w-full rounded-xl border border-slate-300 p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tw-final">نص الإنذار الأخير / المادة القانونية (يظهر في الإنذار الثاني)</Label>
              <textarea id="tw-final" data-testid="target-warn-final-input" rows={3} value={form.target_warn_final ?? ""}
                onChange={(e) => setForm({ ...form, target_warn_final: e.target.value })}
                className="w-full rounded-xl border border-slate-300 p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tw-suspend">نص الإيقاف (عند الإنذار الثالث)</Label>
              <textarea id="tw-suspend" data-testid="target-suspend-text-input" rows={2} value={form.target_suspend_text ?? ""}
                onChange={(e) => setForm({ ...form, target_suspend_text: e.target.value })}
                className="w-full rounded-xl border border-slate-300 p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sig-consent">نص تعهّد التوقيع الإلكتروني (بوابة المندوب)</Label>
              <textarea id="sig-consent" data-testid="signature-consent-text-input" rows={3} value={form.signature_consent_text ?? ""}
                onChange={(e) => setForm({ ...form, signature_consent_text: e.target.value })}
                placeholder="اتركه فارغاً لاستخدام النص الافتراضي"
                className="w-full rounded-xl border border-slate-300 p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <p className="text-xs text-slate-400">تظهر هذه النصوص للمندوب داخل نافذة إنذار عدم تحقيق التارقت. اترك الحقل فارغاً للرجوع للنص الافتراضي.</p>
          </div>
          <div className="pt-2 border-t border-slate-100 sm:col-span-2">
            <PasskeySettingCard />
          </div>
        </div>
  );
  if (embedded) {
    return (
      <div className="space-y-3 font-body" data-testid="notifications-settings-panel">
        <p className="text-xs text-slate-500">{t("dash.modals.settings.desc", { d1: form.days_before, d2: form.second_reminder_days })}</p>
        {body}
        <div className="sticky bottom-0 bg-white py-2 border-t border-slate-100">
          <Button data-testid="save-settings-button" className="bg-blue-600 hover:bg-blue-700 active:scale-95 transition" onClick={() => { const { email_daily_digest_enabled, portal_saudi_id_only, ...rest } = form; onSave(rest); }}>{t("dash.modals.settings.save")}</Button>
        </div>
      </div>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto font-body" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{t("dash.modals.settings.title")}</DialogTitle>
          <DialogDescription className="text-start">
            {t("dash.modals.settings.desc", { d1: form.days_before, d2: form.second_reminder_days })}
          </DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="save-settings-button"
            className="bg-blue-600 hover:bg-blue-700 active:scale-95 transition"
            onClick={() => { const { email_daily_digest_enabled, portal_saudi_id_only, ...rest } = form; onSave(rest); }}>{t("dash.modals.settings.save")}</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.modals.settings.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
